import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchJson, postJson } from '../api';
import { useAuth } from '../lib/auth';
import { trackVote } from '../lib/analytics';
import { formatCount, plural, relativeTime } from '../lib/format';
import { sharePoll } from '../lib/share';
import PollTiles, { captionsFromItems } from './PollTiles';
import { CheckIcon, CommentIcon, MoreIcon, ShareIcon } from './Icons';

export { captionsFromItems };
// Kept for older imports; the tiles now live in PollTiles.
export { default as PollSprite } from './PollTiles';

// Report categories (match backend REPORT_REASONS). Picking one tags the flag
// so moderators can triage by reason in the admin queue.
const REPORT_REASONS = [
  ['child_safety', 'Child safety concern'],
  ['spam', 'Spam'],
  ['nudity', 'Nudity or sexual content'],
  ['harassment', 'Harassment or bullying'],
  ['hate', 'Hate speech'],
  ['violence', 'Violence'],
  ['misinformation', 'Misinformation'],
  ['other', 'Something else'],
];

// The count chip (frame B): coral for undecided polls ("first vote?" /
// "4 votes"), a quiet surface chip with the bare count for everything else.
export function CountChip({ poll, votes }) {
  const total = votes ?? poll.votes_count ?? 0;
  if (poll.undecided) {
    return (
      <span className="flex-none rounded-lg bg-voto-coral px-[9px] py-[5px] text-xs font-semibold text-white">
        {total === 0 ? 'first vote?' : plural(total, 'vote')}
      </span>
    );
  }
  return (
    <span
      aria-label={plural(total, 'vote')}
      className="flex-none rounded-lg bg-voto-surface px-[9px] py-[5px] text-xs font-semibold text-voto-muted"
    >
      {formatCount(total)}
    </span>
  );
}

/**
 * One feed card, frame B: photos edge to edge (tap one to vote), the caption
 * under them, "Name · 6d" and the count chip; share, comments and the
 * moderation menu sit on the byline row.
 */
export default function PollCard({ poll, to, eyebrow = null, byline = null, source = 'feed' }) {
  const auth = useAuth();
  const currentUser = auth?.user || null;
  // Your own poll: you can't vote on it, and you see results without voting.
  const isMine = !!currentUser && poll.author_id != null && currentUser.id === poll.author_id;

  const [vote, setVote] = useState(null); // { votedItemId, voting, items, votesCount }
  const [voteError, setVoteError] = useState('');
  const [shareState, setShareState] = useState('idle'); // idle | copied
  const shareTimer = useRef(null);
  // Inline comments (#56) — comment without opening the poll.
  const [showComment, setShowComment] = useState(false);
  const [draft, setDraft] = useState('');
  const [posting, setPosting] = useState(false);
  const [commentDelta, setCommentDelta] = useState(0);
  const [commentMsg, setCommentMsg] = useState('');
  const [comments, setComments] = useState(null);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentsHasMore, setCommentsHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [reportedComments, setReportedComments] = useState({});
  const COMMENTS_PAGE = 50;
  // Moderation menu (#58).
  const [menuOpen, setMenuOpen] = useState(false);
  const [reportingPoll, setReportingPoll] = useState(false);
  const [reported, setReported] = useState(false);
  const [modMsg, setModMsg] = useState('');

  const itemCount = poll.items_count || poll.items?.length || 1;
  const authorName = poll.author_name || poll.author_handle || null;
  const authorHandle = poll.author_handle || null;
  const items = vote?.items || poll.items;
  const totalVotes = vote ? vote.votesCount : poll.votes_count;
  const revealed = !!vote || isMine;

  useEffect(() => () => { if (shareTimer.current) clearTimeout(shareTimer.current); }, []);

  // If the server says this session already voted, show results on load.
  useEffect(() => {
    if (poll.voted_item_id != null) {
      setVote({ votedItemId: poll.voted_item_id, voting: false, items: poll.items, votesCount: poll.votes_count });
    }
  }, [poll.id, poll.voted_item_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Lazy-load comments the first time the section opens (paginated endpoint,
  // not the whole poll-detail payload).
  useEffect(() => {
    if (!showComment || comments !== null || commentsLoading) return undefined;
    let cancelled = false;
    setCommentsLoading(true);
    fetchJson(`/polls/${poll.id}/comments?limit=${COMMENTS_PAGE}`)
      .then((d) => { if (!cancelled) { setComments(d.comments || []); setCommentsHasMore(!!d.has_more); } })
      .catch(() => { if (!cancelled) setComments([]); })
      .finally(() => { if (!cancelled) setCommentsLoading(false); });
    return () => { cancelled = true; };
  }, [showComment, comments, commentsLoading, poll.id]);

  async function loadMoreComments() {
    if (loadingMore) return;
    setLoadingMore(true);
    try {
      const d = await fetchJson(`/polls/${poll.id}/comments?limit=${COMMENTS_PAGE}&offset=${(comments || []).length}`);
      setComments((cur) => [...(cur || []), ...(d.comments || [])]);
      setCommentsHasMore(!!d.has_more);
    } catch {
      // keep the list; the button stays for a retry
    } finally {
      setLoadingMore(false);
    }
  }

  async function handleVote(item) {
    if (vote || isMine || !item?.id) return;
    setVoteError('');
    const optimisticItems = (poll.items || []).map((it) =>
      it.id === item.id ? { ...it, votes_count: (it.votes_count || 0) + 1 } : it
    );
    const optimisticTotal = (poll.votes_count || 0) + 1;
    setVote({ votedItemId: item.id, voting: true, items: optimisticItems, votesCount: optimisticTotal });
    try {
      const data = await postJson(`/polls/${poll.id}/vote`, { item_id: item.id, source });
      trackVote(source, { poll_id: poll.id });
      setVote({
        votedItemId: data.voted_item_id ?? item.id,
        voting: false,
        items: data.items || optimisticItems,
        votesCount: data.votes_count ?? optimisticTotal,
      });
    } catch (err) {
      if (err.status === 409) {
        setVote({ votedItemId: null, voting: false, items: poll.items, votesCount: poll.votes_count });
        setVoteError('You already voted on this poll.');
      } else {
        setVote(null);
        setVoteError('Unable to record your vote right now.');
      }
    }
  }

  async function handleShare(e) {
    e.preventDefault();
    e.stopPropagation();
    try {
      const { channel } = await sharePoll(poll, { where: 'feed_card' });
      if (channel === 'copy') {
        setShareState('copied');
        if (shareTimer.current) clearTimeout(shareTimer.current);
        shareTimer.current = setTimeout(() => { setShareState('idle'); shareTimer.current = null; }, 1500);
      }
    } catch (_) {
      // Neither the share sheet nor the clipboard is available.
    }
  }

  async function submitComment() {
    const body = draft.trim();
    if (!body || posting) return;
    setPosting(true);
    setCommentMsg('');
    try {
      const created = await postJson(`/polls/${poll.id}/comments`, { body });
      setDraft('');
      setCommentDelta((d) => d + 1);
      setCommentMsg('Posted ✓');
      if (created && created.id != null) setComments((cur) => [created, ...(cur || [])]);
    } catch (err) {
      if (err.status === 401 || err.status === 403) setCommentMsg('Sign in to comment.');
      else if (err.status === 422 && err.detail) setCommentMsg(err.detail);
      else setCommentMsg('Could not post.');
    } finally {
      setPosting(false);
    }
  }

  async function reportPoll(reason) {
    setMenuOpen(false);
    setReportingPoll(false);
    if (reported) return;
    try {
      await postJson(`/polls/${poll.id}/flag`, reason ? { reason } : {});
      setReported(true);
    } catch (_) {
      // fire-and-forget
    }
  }

  async function reportComment(commentId) {
    if (reportedComments[commentId]) return;
    try {
      await postJson(`/comments/${commentId}/flag`, {});
      setReportedComments((m) => ({ ...m, [commentId]: true }));
    } catch (_) {
      // best-effort, mirrors report-poll
    }
  }

  async function muteOrBlock(action) {
    setMenuOpen(false);
    if (!authorHandle) return;
    try {
      await postJson(`/users/${authorHandle}/${action}`, {});
      setModMsg(`${action === 'mute' ? 'Muted' : 'Blocked'} @${authorHandle} ✓`);
    } catch (err) {
      setModMsg(err.status === 401 || err.status === 403 ? 'Sign in first.' : `Could not ${action}.`);
    }
  }

  const commentCount = (poll.comments_count ?? 0) + commentDelta;
  const when = relativeTime(poll.created_at);
  const bylineText = byline ?? [authorName || 'Voto archive', when].filter(Boolean).join(' · ');

  return (
    <article className="bg-voto-bg" data-testid="poll-card">
      <PollTiles
        src={poll.image_url}
        count={itemCount}
        items={items}
        alt={poll.caption || 'Poll image'}
        variant="edge"
        onVote={isMine ? null : handleVote}
        revealed={revealed}
        votedItemId={vote?.votedItemId ?? null}
        totalVotes={totalVotes || 0}
        voting={vote?.voting === true}
      />

      <div className="px-3.5 pb-3 pt-2.5">
        <div className="flex items-start gap-2.5">
          <div className="min-w-0 flex-1">
            {eyebrow && <p className="mb-1 text-[11px] font-semibold text-voto-coral">{eyebrow}</p>}
            {poll.caption && <CaptionText to={to} caption={poll.caption} />}
          </div>
          <CountChip poll={poll} votes={totalVotes} />
        </div>

        <div className="mt-1 flex items-center gap-1 text-xs text-voto-muted">
          {authorHandle && !byline ? (
            <Link to={`/u/${authorHandle}`} className="min-w-0 truncate rounded hover:text-voto-fg">
              {bylineText}
            </Link>
          ) : (
            <span className="min-w-0 truncate">{bylineText}</span>
          )}
          {poll.visibility === 'link' && (
            <span className="ml-1 flex-none rounded bg-voto-surface px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
              link only
            </span>
          )}
          <span className="flex-1" />
          <button
            type="button"
            aria-label={`Comments (${commentCount})`}
            aria-expanded={showComment}
            className="flex flex-none items-center gap-1 rounded-md px-1.5 py-1 transition hover:text-voto-fg"
            onClick={() => setShowComment((s) => !s)}
          >
            <CommentIcon />
            <span>{commentCount}</span>
          </button>
          <button
            aria-label={shareState === 'copied' ? 'Link copied' : 'Share'}
            className={`flex flex-none items-center gap-1 rounded-md px-1.5 py-1 transition ${shareState === 'copied' ? 'text-voto-green' : 'hover:text-voto-fg'}`}
            title={shareState === 'copied' ? 'Link copied' : 'Share'}
            type="button"
            onClick={handleShare}
          >
            {shareState === 'copied' ? (
              <>
                <CheckIcon />
                <span className="font-medium">Copied</span>
              </>
            ) : (
              <ShareIcon />
            )}
          </button>
          <div className="relative flex-none">
            <button
              type="button"
              aria-label="More options"
              aria-expanded={menuOpen}
              className="rounded-md px-1.5 py-1 transition hover:text-voto-fg"
              onClick={() => { setReportingPoll(false); setMenuOpen((o) => !o); }}
            >
              <MoreIcon />
            </button>
            {menuOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => { setMenuOpen(false); setReportingPoll(false); }} aria-hidden="true" />
                <div className="absolute bottom-8 right-0 z-20 w-52 overflow-hidden rounded-xl bg-voto-chrome py-1 shadow-sheet ring-1 ring-voto-line">
                  {reportingPoll ? (
                    <>
                      <p className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-voto-muted">Why are you reporting?</p>
                      {REPORT_REASONS.map(([value, label]) => (
                        <button key={value} type="button" className="block w-full px-3 py-2 text-left text-sm text-voto-fg hover:bg-voto-raised" onClick={() => reportPoll(value)}>
                          {label}
                        </button>
                      ))}
                    </>
                  ) : (
                    <button type="button" className="block w-full px-3 py-2 text-left text-sm text-voto-fg hover:bg-voto-raised" onClick={() => setReportingPoll(true)}>
                      Report poll
                    </button>
                  )}
                  {!reportingPoll && authorHandle && (
                    <>
                      <button type="button" className="block w-full px-3 py-2 text-left text-sm text-voto-fg hover:bg-voto-raised" onClick={() => muteOrBlock('mute')}>
                        Mute @{authorHandle}
                      </button>
                      <button type="button" className="block w-full px-3 py-2 text-left text-sm text-voto-coral hover:bg-voto-raised" onClick={() => muteOrBlock('block')}>
                        Block @{authorHandle}
                      </button>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {(voteError || reported || modMsg) && (
          <p className="mt-1 text-xs font-medium">
            {voteError && <span className="text-voto-coral">{voteError} </span>}
            {reported && <span className="text-voto-green">Reported ✓ </span>}
            {modMsg && <span className="text-voto-muted">{modMsg}</span>}
          </p>
        )}
        {isMine && !voteError && <p className="mt-1 text-xs text-voto-muted">Your poll — here’s how it’s going.</p>}
      </div>

      {showComment && (
        <div className="border-t border-voto-line px-3.5 py-3">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Add a comment…"
            aria-label="Add a comment"
            rows={2}
            maxLength={1000}
            className="w-full resize-none rounded-lg border border-voto-line px-2.5 py-1.5 text-sm focus:border-voto-coral focus:outline-none"
          />
          <div className="mt-1.5 flex items-center gap-2">
            <button
              type="button"
              disabled={!draft.trim() || posting}
              onClick={submitComment}
              className="rounded-lg bg-voto-coral px-3 py-1 text-sm font-semibold text-white transition hover:bg-voto-coralDeep disabled:opacity-50"
            >
              {posting ? 'Posting…' : 'Post'}
            </button>
            {commentMsg && <span className="text-xs text-voto-muted">{commentMsg}</span>}
          </div>

          {commentsLoading && comments === null ? (
            <p className="mt-3 text-xs text-voto-muted">Loading comments…</p>
          ) : comments && comments.length > 0 ? (
            <ul className="mt-3 space-y-2.5">
              {comments.map((c) => (
                <li key={c.id ?? `${c.display_name}-${c.created_at}`} className="text-sm">
                  <span className="font-semibold text-voto-fg">{c.display_name || 'Voto Memories'}</span>
                  {c.created_at && <span className="ml-1.5 text-xs text-voto-muted">{relativeTime(c.created_at)}</span>}
                  {c.id != null && (
                    <button
                      type="button"
                      className="ml-1.5 text-xs text-voto-muted hover:text-voto-fg disabled:opacity-60"
                      onClick={() => reportComment(c.id)}
                      disabled={!!reportedComments[c.id]}
                    >
                      {reportedComments[c.id] ? '· reported' : '· report'}
                    </button>
                  )}
                  <p className="text-voto-fg/90">{c.body}</p>
                </li>
              ))}
              {commentsHasMore && (
                <li>
                  <button type="button" className="text-xs font-medium text-voto-coral hover:underline disabled:opacity-60" onClick={loadMoreComments} disabled={loadingMore}>
                    {loadingMore ? 'Loading…' : 'Load more comments'}
                  </button>
                </li>
              )}
            </ul>
          ) : comments ? (
            <p className="mt-3 text-xs text-voto-muted">No comments yet — be the first.</p>
          ) : null}
        </div>
      )}
    </article>
  );
}

// The caption links to the poll when a `to` is given (feed).
function CaptionText({ to, caption }) {
  const text = <h3 className="text-[15px] font-semibold leading-[1.35] text-voto-fg">{caption}</h3>;
  if (!to) return text;
  return (
    <Link to={to} className="block rounded transition hover:opacity-90">
      {text}
    </Link>
  );
}
